import { Link } from 'react-router-dom';
import Footer from '../components/Footer.jsx';
import Header from '../components/Header.jsx';
import Section, { Eyebrow } from '../components/Section.jsx';
import Seo from '../components/Seo.jsx';
import { getFeaturedProperty } from '../lib/property-config.js';
import { CONTACT_EMAIL } from '../lib/site-config.js';

// Review restructure (2026-06-12): the page answers three different trust
// questions — what is this, who runs it, can I believe the numbers — so it
// gets three kicker-labeled sections instead of one undifferentiated essay.

export default function About() {
  const property = getFeaturedProperty();
  return (
    <div className="mx-auto max-w-brief px-4 pt-6 sm:px-6">
      <Seo
        title="About · Charleston.Vacations"
        description="What the daily Charleston brief is, who writes it, and where the data comes from."
      />
      <Header />
      <div className="mt-8 border-b border-navy-100 pb-6">
        <h1 className="font-display text-3xl font-semibold text-navy-900 sm:text-4xl">
          About Charleston.Vacations
        </h1>
      </div>

      <Section eyebrow="The brief" title="One page, one Charleston day">
        <div className="prose-navy space-y-4">
          <p>
            Charleston.Vacations is a daily conditions and what-to-do read for visitors
            to Charleston, SC. A new brief every morning by 6:30 ET. It reads like a sharp
            local helping you plan one specific day, not a marketing site and not a
            utility tool.
          </p>
          <p>
            Pick a persona at the top of today's brief to reorder the page around what
            you care about. With Kids plans the day around little ones, heat windows,
            naps, and early evenings included. Beach Bum leads with surf and tide,
            Foodie leads with dining, On the Water leads with marine and ramps, and
            First-Timer leads with picks and orientation.
          </p>
        </div>
      </Section>

      <Section eyebrow="The hosts" title={property ? `Written by ${property.hosts}` : 'Written by your hosts'}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          {property?.hostPhoto && (
            <img
              src={property.hostPhoto}
              width="112"
              height="112"
              alt={property.hosts}
              className="h-28 w-28 shrink-0 rounded-full border border-navy-100 object-cover"
            />
          )}
          <div className="prose-navy space-y-4">
            <p>
              We're {property?.hosts || 'James & Natalie'}, and the brief began as the
              morning note we kept for guests staying at{' '}
              {property ? `${property.name}, our home in ${property.location}` : 'our home in Mount Pleasant, SC'}.
              The local details still come from our daily update: beach flags, events,
              cruise ship arrivals.
            </p>
            <p>
              Book the house direct and you deal with us, not a platform inbox: you'll
              have our cell from booking to checkout, and{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-navy-900 underline">
                {CONTACT_EMAIL}
              </a>{' '}
              reaches us anytime.
            </p>
          </div>
        </div>
      </Section>

      <Section eyebrow="The data" title="Where the numbers come from">
        <div className="prose-navy space-y-4">
          <p>
            Data comes from the National Weather Service, NOAA tides and currents,
            NDBC buoys, and AirNow. Each morning's brief is drafted with AI from that
            data and run through automated consistency checks before it publishes.
          </p>
          {/* The footer already carries "not navigation guidance"; this line keeps
              only the voice the footer can't. */}
          <p className="text-sm text-navy-500">
            Conditions are estimates. If lifeguards or USCG say otherwise, listen to
            them.
          </p>
        </div>
      </Section>

      {/* Bridge to the booking page so the trust page never dead-ends on a
          disclaimer. */}
      {property && (
        <div className="mt-4 border-t border-navy-100 pt-6">
          <Eyebrow text="Stay with us" />
          <p className="prose-navy mt-2 text-navy-800">
            The brief is written from the porch at {property.name}.
          </p>
          <Link
            to={`/stay/${property.slug}`}
            className="mt-4 inline-block rounded-lg bg-navy-900 px-4 py-2 text-sm font-medium text-cream-50 no-underline hover:bg-navy-800"
          >
            See the house <span aria-hidden="true">›</span>
          </Link>
        </div>
      )}
      <Footer />
    </div>
  );
}
